/**
 * Theme manager — core (framework-free) logic. `useTheme` (theme.tsx-adjacent hook in this
 * same package, see `useTheme.ts`) is the only consumer most code needs; this module is
 * split out so the persistence/resolution logic can be unit-tested without a DOM (WS theme
 * system, docs/DECISIONS.md 2026-07-31).
 *
 * The inline snippet in `index.html` duplicates the storage-key + resolution logic in ~5
 * lines of vanilla JS so the *first paint* already has the right `data-theme` — no flash of
 * the wrong theme while React boots. Keep that snippet and this module in sync if the theme
 * list ever changes.
 */

export const THEME_STORAGE_KEY = 'awwyeah:theme';

/** Switcher groups. With ten themes the flat list stopped being scannable (owner call,
 * 2026-08-01), so every option carries the heading it sorts under. */
export type ThemeGroup = 'Classic' | 'Fun' | 'Wild';

export const THEME_GROUPS: readonly ThemeGroup[] = ['Classic', 'Fun', 'Wild'];

/** Built-in custom themes, in switcher display order. Names/descriptions also drive the
 * "Appearance" section copy in Settings. */
export const CUSTOM_THEMES = [
  {
    id: 'bubblegum',
    label: 'Bubblegum',
    description: 'Playful pale pink with a candy-bright accent.',
    group: 'Fun',
  },
  {
    id: 'cosmos',
    label: 'Cosmos',
    description: 'Deep space and nebula violet, starlight type.',
    group: 'Wild',
  },
] as const satisfies readonly {
  id: string;
  label: string;
  description: string;
  group: ThemeGroup;
}[];

export type CustomThemeId = (typeof CUSTOM_THEMES)[number]['id'];

/** What's stored/selected: `'system'` follows the OS, everything else is applied directly. */
export type ThemePreference = 'system' | 'light' | 'dark' | CustomThemeId;

/** What ends up on `data-theme` — never `'system'`, it's always resolved to a concrete theme. */
export type ResolvedTheme = Exclude<ThemePreference, 'system'>;

export const THEME_PREFERENCES: readonly ThemePreference[] = [
  'system',
  'light',
  'dark',
  ...CUSTOM_THEMES.map((t) => t.id),
];

export function isThemePreference(value: string | null | undefined): value is ThemePreference {
  return value != null && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/** Minimal `Storage` shape we depend on, so tests can pass a fake instead of real localStorage. */
export type ThemeStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** Reads the persisted preference, falling back to `'system'` for anything missing/invalid
 * (unknown values can show up after a rollback that removes a theme). */
export function readStoredThemePreference(storage: ThemeStorage): ThemePreference {
  let raw: string | null = null;
  try {
    raw = storage.getItem(THEME_STORAGE_KEY);
  } catch {
    // Storage can throw (private browsing, quota) — fall back silently.
    return 'system';
  }
  return isThemePreference(raw) ? raw : 'system';
}

export function writeStoredThemePreference(
  storage: ThemeStorage,
  preference: ThemePreference,
): void {
  try {
    storage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Best-effort persistence; a failed write just means it reverts to 'system' next launch.
  }
}

/** `'system'` resolves via the OS dark-mode signal; every other preference passes through
 * unchanged (a custom theme is never "dark" or "light" as far as the OS is concerned). */
export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  return preference === 'system' ? (systemPrefersDark ? 'dark' : 'light') : preference;
}

/** Applies the resolved theme to the DOM. This is the one place `data-theme` gets written at
 * runtime (mirrors the pre-paint inline snippet in index.html). */
export function applyResolvedTheme(root: HTMLElement, resolved: ResolvedTheme): void {
  root.setAttribute('data-theme', resolved);
}

/**
 * Keeps the PWA chrome color in sync with the active theme's surface color. Reads the
 * *computed* `--app-surface` value rather than a JS color table, so it can never drift from
 * the CSS themes in tokens.css.
 */
export function syncThemeColorMeta(doc: Document, root: HTMLElement): void {
  const meta = doc.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const surface = doc.defaultView?.getComputedStyle(root).getPropertyValue('--app-surface').trim();
  if (surface) meta.setAttribute('content', surface);
}
